import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {of, ReplaySubject} from 'rxjs';
import {CommissionImportRow} from '../../../../shared/interface/commission-import-row';
import {delay, first, skipUntil, takeUntil, tap} from 'rxjs/operators';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {AuthService} from '../../../../shared/services/auth.service';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {CommissionRedirectionOfwcaRelations} from '../../../../shared/interface/commission-redirection-ofwca-relations';
import {CommissionRedirectionService} from '../../../../shared/services/commission-redirection.service';
import {
  COMMISSION_IMPORT_ROW_STATUS_NEW,
  COMMISSION_IMPORT_ROW_STATUS_REJECTED
} from '../../../../shared/services/dict.service';
import {Dictionary} from '../../../../shared/interface/dictionary';
import {HttpErrorResponse} from '@angular/common/http';
import {CommissionImportRowService} from '../../../../shared/services/commission-import-row.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormHelperService} from '../../../../shared/services/form-helper.service';
import {Contract} from '../../../../shared/interface/contract';

@Component({
  selector: 'app-commission-details-form',
  templateUrl: './commission-details-form.component.html',
  styleUrls: ['./commission-details-form.component.scss']
})
export class CommissionDetailsFormComponent implements OnInit, OnDestroy {
  @Input() commissionImportRow: ReplaySubject<CommissionImportRow>;
  @Output() closeModification = new EventEmitter<any>();

  readonly COMMISSION_IMPORT_ROW_STATUS_REJECTED = COMMISSION_IMPORT_ROW_STATUS_REJECTED;
  readonly COMMISSION_IMPORT_ROW_STATUS_NEW = COMMISSION_IMPORT_ROW_STATUS_NEW;

  commissionImportRowObject: CommissionImportRow;
  userPermissions: ReplaySubject<UserPermissions>;
  processingRequest = false;
  commissionImportRowForm: FormGroup;
  commissionImportRowFormValid = false;
  riskControl: FormControl;
  riskPackControl: FormControl;
  policyTypeControl: FormControl;
  ofwcaControl: FormControl;
  contractControl: FormControl;
  partnerControl: FormControl;
  partnerAssetsRegionalControl: FormControl;
  ratePaidControl: FormControl;
  rejectedControl: FormControl;
  statusControl: FormControl;
  showAllContractsControl: FormControl;
  collectionControl: FormControl;
  formErrors: any = {};
  availableContracts: ReplaySubject<Contract[]> = new ReplaySubject<Contract[]>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private auth: AuthService,
    private fb: FormBuilder,
    private commissionRedirectionService: CommissionRedirectionService,
    private commissionImportRowService: CommissionImportRowService,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionImportRowForm = fb.group({
      risk: this.riskControl = fb.control(''),
      riskPack: this.riskPackControl = fb.control(''),
      policyType: this.policyTypeControl = fb.control('', [Validators.required]),
      ofwca: this.ofwcaControl = fb.control(''),
      contract: this.contractControl = fb.control(''),
      partner: this.partnerControl = fb.control(''),
      partnerAssetsRegional: this.partnerAssetsRegionalControl = fb.control(''),
      ratePaid: this.ratePaidControl = fb.control(''),
      rejected: this.rejectedControl = fb.control(false),
      status: this.statusControl = fb.control(''),
      showAllContracts: this.showAllContractsControl = fb.control(false),
      collection: this.collectionControl = fb.control('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.commissionImportRow
      .pipe(takeUntil(this.destroyed$))
      .subscribe((commissionImportRow: CommissionImportRow) => {
        this.commissionImportRowObject = commissionImportRow;
        this.initialize();
      });

    this.commissionImportRowForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.commissionImportRowFormValid = this.commissionImportRowForm.valid)
    ;

    this.ofwcaControl.valueChanges
      .pipe(
        skipUntil(this.initialized),
        takeUntil(this.destroyed$)
      ).subscribe((ofwcaId?: string) => {
        if (!ofwcaId) {
          return;
        }

        this.loadOfwcaRelations(ofwcaId, true);
      });
  }

  loadOfwcaRelations(ofwcaId: string, setNewValues = false): void {
    this.commissionRedirectionService.getOfwcaRelations(
      this.commissionImportRowObject.company?.id,
      ofwcaId,
    ).subscribe((ofwcaRelations: CommissionRedirectionOfwcaRelations) => {
      if (setNewValues) {
        this.partnerControl.setValue(ofwcaRelations.newPartnerId ?? '');
        this.partnerAssetsRegionalControl.setValue(ofwcaRelations.newPartnerAssetsRegionalId ?? '');
        this.contractControl.setValue(ofwcaRelations.newContractId ?? '');
      }

      this.availableContracts.next(ofwcaRelations.availableContracts);
    });
  }

  initialize(): void {
    of(this.commissionImportRowObject)
      .pipe(
        delay(0),
        tap((commissionImportRow: CommissionImportRow) => {
          this.commissionImportRowForm.setValue({
            risk: commissionImportRow?.risk?.id ?? '',
            riskPack: commissionImportRow?.riskPack ?? '',
            policyType: commissionImportRow?.policyType?.id ?? '',
            ofwca: commissionImportRow?.ofwca?.uuid ?? '',
            contract: commissionImportRow?.contract?.uuid ?? '',
            partner: commissionImportRow?.partner?.uuid ?? '',
            partnerAssetsRegional: commissionImportRow?.partnerAssetsRegional?.uuid ?? '',
            ratePaid: commissionImportRow?.ratePaid ?? '',
            rejected: commissionImportRow?.status?.key === COMMISSION_IMPORT_ROW_STATUS_REJECTED,
            status: commissionImportRow?.status?.id ?? '',
            showAllContracts: true,
            collection: commissionImportRow.collection,
          });
          this.loadOfwcaRelations(commissionImportRow?.ofwca?.uuid);
        }),
        delay(0),
        tap(() => {
          this.commissionImportRowForm.markAllAsTouched();
          this.initialized.next(true);
        })
      ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  cancel(): void {
    this.closeModification.emit();
  }

  save(): void {
    if (!this.commissionImportRowForm.valid) {
      this.commissionImportRowForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.commissionImportRowForm, this.formErrors);
    this.formErrors = {};

    this.commissionImportRowService.update(this.commissionImportRowForm.value, this.commissionImportRowObject.uuid)
      .pipe(
        first()
      )
      .subscribe(() => {
        this.snack.open('Pomyślnie zaktualizowano prowizję', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.closeModification.emit();
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;
        this.snack.open('Podczas aktualizacji prowizji wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.commissionImportRowForm, this.formErrors);
        }
      })
    ;
  }

  statusChanged(status: Dictionary): void {
    this.rejectedControl.setValue(status?.key === COMMISSION_IMPORT_ROW_STATUS_REJECTED);
  }
}
